import React, { useState } from "react";

/**
 * Passwordless Migration Banner Component
 *
 * Displays an informational banner about the passwordless authentication migration.
 * Can be dismissed by the user and preference is stored in localStorage.
 */
const PasswordlessBanner = ({ className = "" }) => {
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check if user has already dismissed the banner
    return localStorage.getItem("passwordless_banner_dismissed") === "true";
  });

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem("passwordless_banner_dismissed", "true");
  };

  if (isDismissed) {
    return null;
  }

  return (
    <div
      className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-semibold text-blue-900 mb-1">
            🎉 We've gone passwordless!
          </h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>
              We now use <strong>magic links</strong> for a faster, more secure sign-in
              experience. No more passwords to remember!
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-xs text-blue-700">
              <li>More secure - no password to guess or steal</li>
              <li>Faster login - just click the link in your email</li>
              <li>Works seamlessly across all your devices</li>
            </ul>
          </div>
        </div>
        <div className="ml-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleDismiss}
            className="inline-flex text-blue-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md p-1.5 transition-colors"
            aria-label="Dismiss banner"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordlessBanner;
